import { useQuestCompletions } from "@/components/QuestsV2/useQuestCompletions";
import { Action, ControlType, useAnalytics } from "@/context/AnalyticsProvider";
import { useAccessToken } from "@/hooks";
import { authorizedPost } from "@/utils/request";
import {
  Body,
  Box,
  ButtCon,
  Button,
  Heading,
  Icon,
  Modal,
  Stack,
} from "@biom3/react";
import { MODAL_WIDTH, appConfig } from "@constants";
import imageCompression from "browser-image-compression";
import { useCallback, useEffect, useRef, useState } from "react";
import { fileToBase64 } from "./LinkedAccountLogin";

import Image from "next/image";
import { AppStoreImageExample } from "./AppStoreImageExample";

type AppStoreImageValidationModalProps = {
  visible: boolean;
  onClose: () => void;
  validationType?: "download" | "pre-registration";
  questId: string;
  description?: string;
  gameUid: string;
  gameId: string;
};

const compressImage = async (image: File) => {
  const options = {
    maxSizeMB: 1.5,
    useWebWorker: true,
    initialQuality: 0.8,
    maxWidthOrHeight: 1920,
  };

  try {
    const compressedFile = await imageCompression(image, options);
    return compressedFile;
  } catch (error) {
    console.error("Failed to compress image:", error);
    return null;
  }
};

export function AppStoreImageValidationModal({
  visible,
  onClose,
  validationType,
  questId,
  gameUid,
  gameId,
}: AppStoreImageValidationModalProps) {
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const getAccessToken = useAccessToken();
  const { trackV3 } = useAnalytics();
  const { mutate: invalidateQuestCompletions } = useQuestCompletions(gameId);

  useEffect(() => {
    if (visible) {
      trackV3({
        object: "Quest",
        action: Action.OPENED,
        extras: {
          questId,
          gameUid,
          validationType: validationType || "unknown",
          section: "AppStoreImageValidationModal",
        },
      });
    }
  }, [visible, questId, gameUid, validationType, trackV3]);

  const handleClose = useCallback(() => {
    // Track modal closed
    trackV3({
      object: "Quest",
      action: Action.PRESSED,
      extras: {
        questId,
        gameUid,
        validationType: validationType || "unknown",
        section: "AppStoreImageValidationModal",
        controlType: ControlType.BUTTON,
        control: "Close",
      },
    });

    setSelectedImage(null);
    setImageError(null);
    setIsUploading(false);
    setIsSuccess(false);
    setSubmitError(null);
    onClose();
  }, [onClose, questId, gameUid, validationType, trackV3]);

  const onSubmit = useCallback(
    async (imageFile: File) => {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("No access token available");
      }

      // if file is over 1MB we should try to compress it
      let requiredImageFile = imageFile;
      if (imageFile.size > 1024 * 1024) {
        const compressedFile = await compressImage(imageFile);

        if (!compressedFile) {
          console.error("Failed to compress image");
        } else {
          requiredImageFile = compressedFile;
        }
      }

      const base64DataUrl = await fileToBase64(requiredImageFile);
      const base64Data = base64DataUrl.split(",")[1];

      const requestBody = {
        imageName: imageFile.name,
        imageType: imageFile.type,
        base64Data,
        sizeBytes: imageFile.size,
        quest_id: questId,
      };

      const res = await authorizedPost(
        "/v1/quests/validate-app-store-action",
        requestBody,
        accessToken,
      );

      return res;
    },
    [questId, getAccessToken],
  );

  const title =
    validationType === "download"
      ? "Confirm App store download"
      : "Confirm App store pre-registration";

  const getExampleImages = (): {
    iosExample: string;
    googleExample: string;
    epicExample: string;
  } => {
    let inputValidationType = validationType;

    if (!inputValidationType && gameUid === "fates") {
      inputValidationType = "pre-registration";
    }

    switch (inputValidationType) {
      case "download":
        return {
          iosExample: "/images/quests/mobile-app-store.webp",
          googleExample: "/images/quests/google-play-download.webp",
          epicExample: "/images/quests/epic-download.webp",
        };
      case "pre-registration":
        return {
          iosExample: "/images/quests/ios-mobile-pre-reg.webp",
          googleExample: "/images/quests/google-play-mobile-pre-reg.webp",
          epicExample: "/images/quests/epic-pre-reg.webp",
        };
      default:
        return {
          iosExample: "",
          googleExample: "",
          epicExample: "",
        };
    }
  };

  const { iosExample, googleExample, epicExample } = getExampleImages();

  const description =
    validationType === "download"
      ? "Please upload an image of your purchase history that includes the game"
      : "Upload an image of your pre-registration with this game ";

  const links =
    validationType === "download" ? (
      <></>
    ) : (
      <>
        <AppStoreImageExample text="Play store" imageUrl={googleExample} /> and{" "}
        <AppStoreImageExample text="App store" imageUrl={iosExample} /> and{" "}
        <AppStoreImageExample text="Epic Games store" imageUrl={epicExample} />
      </>
    );

  const handleImageSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!allowedTypes.includes(file.type)) {
        setImageError("Please select a valid image file (JPEG or PNG).");
        trackV3({
          object: "Quest",
          action: Action.FAILED,
          extras: {
            questId,
            gameUid,
            validationType: validationType || "unknown",
            section: "AppStoreImageValidationModal",
            error: "Invalid file type",
            fileType: file.type,
          },
        });
        return;
      }

      const maxSize = 5 * 1024 * 1024; // 3MB
      if (file.size > maxSize) {
        setImageError("Image file size must be less than 5MB.");
        trackV3({
          object: "Quest",
          action: Action.FAILED,
          extras: {
            questId,
            gameUid,
            validationType: validationType || "unknown",
            section: "AppStoreImageValidationModal",
            error: "File too large",
            fileSize: file.size,
          },
        });
        return;
      }

      setSelectedImage(file);
      setImageError(null);

      // Track successful image selection
      trackV3({
        object: "Quest",
        action: Action.PRESSED,
        extras: {
          questId,
          gameUid,
          validationType: validationType || "unknown",
          section: "AppStoreImageValidationModal",
          controlType: ControlType.BUTTON,
          control: "FileInput",
          controlLabel: "Add Image",
        },
      });
    },
    [questId, gameUid, validationType, trackV3],
  );

  const handleRemoveImage = useCallback(() => {
    trackV3({
      object: "Quest",
      action: Action.PRESSED,
      extras: {
        questId,
        gameUid,
        validationType: validationType || "unknown",
        section: "AppStoreImageValidationModal",
        controlType: ControlType.BUTTON,
        control: "RemoveImage",
        controlLabel: "Remove Image",
      },
    });

    setSelectedImage(null);
    setImageError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [questId, gameUid, validationType, trackV3]);

  const handleSubmit = useCallback(async () => {
    if (!selectedImage) {
      setImageError("Please select an image to upload.");
      return;
    }

    setImageError(null);
    setSubmitError(null);
    setIsUploading(true);

    // Track validation attempt
    trackV3({
      object: "Quest",
      action: Action.PRESSED,
      extras: {
        questId,
        gameUid,
        validationType: validationType || "unknown",
        section: "AppStoreImageValidationModal",
        controlType: ControlType.BUTTON,
        control: "Submit",
        controlLabel: "Submit",
      },
    });

    try {
      const response = await onSubmit(selectedImage);

      if (response?.valid) {
        setIsSuccess(true);

        // Track successful validation
        trackV3({
          object: "Quest",
          action: Action.SUCCEEDED,
          extras: {
            questId,
            gameUid,
            validationType: validationType || "unknown",
            section: "AppStoreImageValidationModal",
          },
        });
        invalidateQuestCompletions();
      } else {
        setSubmitError(
          "Validation failed. Please ensure your image clearly shows the required information.",
        );

        // Track validation failure
        trackV3({
          object: "Quest",
          action: Action.FAILED,
          extras: {
            questId,
            gameUid,
            validationType: validationType || "unknown",
            section: "AppStoreImageValidationModal",
            error: "Validation failed",
          },
        });
      }
    } catch (error) {
      console.error("Failed to submit image:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to submit image. Please try again.";

      setSubmitError(errorMessage);

      // Track submission error
      trackV3({
        object: "Quest",
        action: Action.FAILED,
        extras: {
          questId,
          gameUid,
          validationType: validationType || "unknown",
          section: "AppStoreImageValidationModal",
          error: errorMessage,
        },
      });
    } finally {
      setIsUploading(false);
    }
  }, [
    selectedImage,
    onSubmit,
    questId,
    gameUid,
    validationType,
    trackV3,
    invalidateQuestCompletions,
  ]);

  return (
    <>
      <Modal
        visible={visible}
        onCloseModal={handleClose}
        showBgOverlay={false}
        outsideClicksClose={false}
      >
        <Modal.Content
          sx={{
            d: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: "base.borderRadius.x10",
            position: "relative",
            overflowY: "hidden",
            background: "base.color.brand.2",
            backgroundImage: `url("${appConfig.ASSET_BASE_URL}/images/quests/follow-confirmation-background.svg")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "top",
            maxHeight: "700px",
            width: {
              default: "calc(100dvw - 40px)",
              small: MODAL_WIDTH,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "base.spacing.x4",
              width: "100%",
            }}
          >
            <Box
              sx={{
                mt: "base.spacing.x28",
                mb: "base.spacing.x6",
                display: "flex",
                justifyContent: "center",
                gap: "base.spacing.x4",
                alignItems: "center",
              }}
            >
              <Image
                src="/images/quests/ios-app-store.webp"
                alt="iOS App Store Logo"
                width={64}
                height={64}
              />
              <Image
                src="/images/quests/google-play-logo.webp"
                alt="Google Play Logo"
                width={64}
                height={64}
              />
              <Image
                src="/images/quests/epic-games-logo.svg"
                alt="Epic Games Logo"
                width={64}
                height={64}
              />
            </Box>

            <Heading
              size="medium"
              weight="bold"
              sx={{ mx: "base.spacing.x12", textAlign: "center" }}
            >
              {isSuccess ? "Success!" : title}
            </Heading>

            <Body
              sx={{
                px: "base.spacing.x10",
                color: "base.color.text.body.secondary",
                textAlign: "center",
                fontSize: "base.text.body.small.regular.fontSize",
              }}
            >
              {isSuccess ? (
                "Your image has been successfully submitted and validated"
              ) : (
                <>
                  {description}
                  {links}
                </>
              )}
            </Body>

            {/* Only show upload section if not in success state */}
            {!isSuccess && (
              <Stack
                sx={{
                  width: "100%",
                  alignItems: "center",
                  gap: "base.spacing.x1",
                }}
              >
                {/* File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleImageSelect}
                  style={{ display: "none" }}
                />

                {gameUid === "fates" && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "base.spacing.x2",
                      alignItems: "center",
                    }}
                  >
                    <a
                      href="https://apps.apple.com/us/app/might-magic-fates-tcg/id6747573541"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ cursor: "pointer" }}
                    >
                      <Image
                        src="/images/quests/ios-download.png"
                        alt="App Store Image Validation"
                        width={130}
                        height={110}
                      />
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.ubisoft.mightmagic.fates.tcg"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ cursor: "pointer" }}
                    >
                      <Image
                        src="/images/quests/google-download.png"
                        alt="Google Play Image Validation"
                        width={150}
                        height={200}
                      />
                    </a>
                  </Box>
                )}
                {/* Image Selection Button - Only show when no image is selected */}
                {!selectedImage && (
                  <Button
                    variant="tertiary"
                    size="medium"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    sx={{
                      width: "296px",
                      border: "1px dashed",
                      borderColor: "base.color.translucent.standard.400",
                      color: "base.color.text.body.secondary",
                      "&:hover": {
                        borderColor: "base.color.accent.2",
                        color: "base.color.accent.2",
                      },
                    }}
                  >
                    <Icon icon="Add" sx={{ mr: "base.spacing.x2" }} />
                    Add Image
                  </Button>
                )}

                {/* Image Preview */}
                {selectedImage && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "base.spacing.x3",
                      width: "296px",
                      padding: "base.spacing.x3",
                      borderRadius: "base.borderRadius.x4",
                      border: "1px solid",
                      borderColor: "base.color.translucent.standard.400",
                      backgroundColor: "base.color.translucent.standard.100",
                    }}
                  >
                    <Box
                      sx={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "base.borderRadius.x2",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={URL.createObjectURL(selectedImage)}
                        alt="Selected"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: "0", overflow: "hidden" }}>
                      <Body
                        size="small"
                        sx={{
                          color: "base.color.text.body.primary",
                          fontWeight: "base.text.body.small.bold.fontWeight",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "block",
                        }}
                      >
                        {selectedImage.name}
                      </Body>
                      <Body
                        size="xSmall"
                        sx={{
                          color: "base.color.text.body.secondary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "block",
                        }}
                      >
                        Image attached
                      </Body>
                    </Box>
                    <ButtCon
                      icon="Close"
                      variant="tertiary"
                      onClick={handleRemoveImage}
                      disabled={isUploading}
                      sx={{
                        flexShrink: 0,
                        width: "24px",
                        height: "24px",
                        ml: "base.spacing.x2",
                        "&:hover": {
                          backgroundColor:
                            "base.color.translucent.standard.300",
                        },
                      }}
                    />
                  </Box>
                )}

                {/* Error Messages */}
                {imageError && (
                  <Body
                    size="small"
                    sx={{
                      color: "base.color.status.fatal.bright",
                      textAlign: "center",
                      maxWidth: "296px",
                    }}
                  >
                    {imageError}
                  </Body>
                )}

                {submitError && (
                  <Body
                    size="small"
                    sx={{
                      color: "base.color.status.fatal.bright",
                      textAlign: "center",
                      maxWidth: "296px",
                    }}
                  >
                    {submitError}
                  </Body>
                )}

                {/* File Format Info */}
                {!selectedImage && (
                  <Body
                    size="xSmall"
                    sx={{
                      color: "base.color.text.body.secondary",
                      textAlign: "center",
                      maxWidth: "296px",
                    }}
                  >
                    Supported formats: JPEG or PNG (max 5MB)
                  </Body>
                )}
              </Stack>
            )}
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              p: "base.spacing.x4",
              mt: "auto",
            }}
          >
            {!isSuccess && (
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!selectedImage || isUploading}
                sx={{ width: "100%", mb: "base.spacing.x2" }}
                size="large"
              >
                {isUploading ? (
                  <>
                    <Icon
                      icon="Loading"
                      sx={{
                        mr: "base.spacing.x2",
                        animation: "spin 1s linear infinite",
                      }}
                    />
                    Uploading...
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            )}

            {isSuccess && (
              <Button
                variant="primary"
                onClick={handleClose}
                sx={{ width: "100%", mb: "base.spacing.x2" }}
                size="large"
              >
                Complete
              </Button>
            )}

            {!isSuccess && (
              <Button
                variant="tertiary"
                onClick={handleClose}
                disabled={isUploading}
                sx={{ width: "100%" }}
                size="large"
              >
                Cancel
              </Button>
            )}
          </Box>

          {/* Close Button */}
          <ButtCon
            icon="Close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: "base.spacing.x4",
              right: "base.spacing.x4",
            }}
            variant="tertiary"
          />
        </Modal.Content>
      </Modal>
    </>
  );
}
